import React from 'react'
import classnames from 'classnames/bind'
import Icon, { IconSize } from '../icon'
import Text from '../text'
import CardContainer from '../cardContainer'
import { ColorNames } from '../../../constants/colorVars'
import styles from './styles.scss'

const cx = classnames.bind(styles)

export interface UtilityCardCta {
  label: string
  onClick: () => void
  /** Show the trailing chevron. Defaults to true. */
  chevron?: boolean
}

export interface UtilityCardProps {
  /**
   * FontAwesome icon shown in the leading light-info circle. No circle is
   * rendered when omitted.
   */
  iconName?: string
  /** Size of the leading icon. Defaults to 'large'. */
  iconSize?: IconSize
  title: string
  subtitle?: string
  /**
   * Extra content rendered under the subtitle in the content column
   * (e.g. a row of chips).
   */
  children?: React.ReactNode
  /**
   * Right-aligned bold CTA link; stacks below the content column on mobile.
   */
  cta?: UtilityCardCta
  /**
   * Chrome around the card. `'card'` (default) is the white, bordered,
   * rounded look. `'flat'` drops the border and radius so the card blends
   * into a band whose background the consumer owns.
   */
  variant?: 'card' | 'flat'
  /** Automation ID of the card content root; the CTA renders `{elmId}-cta`. */
  elmId: string
  /** Automation ID for the CardContainer chrome. Defaults to `elmId`. */
  cardElmId?: string
}

/**
 * UtilityCard — call-out card layout (Figma "utility card"): leading icon in
 * a light circle, title + subtitle (+ optional children below), and an
 * optional right-aligned CTA link.
 */
const UtilityCard = ({
  iconName,
  iconSize = 'large',
  title,
  subtitle,
  children,
  cta,
  variant = 'card',
  elmId,
  cardElmId,
}: UtilityCardProps) => {
  const isFlat = variant === 'flat'

  return (
    <CardContainer
      borderStyle={isFlat ? 'none' : 'line'}
      borderRadius={isFlat ? 'none' : 'medium'}
      backgroundColor={isFlat ? 'unset' : undefined}
      padding='large'
      elmId={cardElmId ?? elmId}
    >
      <div className={cx('utility-card')} data-elm-id={elmId}>
        {iconName && (
          <div className={cx('icon-circle')}>
            <Icon
              name={iconName}
              iconStyle='solid'
              size={iconSize}
              color={ColorNames.Info}
            />
          </div>
        )}
        <div className={cx('content')}>
          <Text.Header level={3} compact>
            {title}
          </Text.Header>
          {subtitle && (
            <Text.Body size='normal' color={ColorNames.Gray}>
              {subtitle}
            </Text.Body>
          )}
          {children}
        </div>
        {cta && (
          <button
            type='button'
            className={cx('cta')}
            onClick={cta.onClick}
            data-elm-id={`${elmId}-cta`}
          >
            {cta.label}
            {cta.chevron !== false && (
              <Icon
                name='chevron-right'
                iconStyle='solid'
                size='small'
                color='inherit'
              />
            )}
          </button>
        )}
      </div>
    </CardContainer>
  )
}

export default UtilityCard
